import { Box, Button, Grid, Typography } from '@mui/material';
import { CitationBreadcrumbs, CitationSnackbar } from './components';
import { CitationRootSx } from './styles';
import {
  getWindowInnerHight,
  openNewTab
} from 'src/common/config/FunctionHelper';
import { keywordOverviewTexts } from 'src/common/config/TextMessage';
import { GMBTutorialLinks, localSeoTexts } from '../localSeo/localSeoTexts';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useCitation } from './Providers/CitationProvider';
import { useUserTypeAndPlan } from 'src/hooks/useUserTypeAndPlan';
import { RootState } from 'src/redux/store';
import { useSelector } from 'react-redux';

const AllowedUI = ({ CTAButton }: { CTAButton: React.ReactNode }) => {
  return (
    <>
      <CitationSnackbar />

      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={4}
      >
        <Grid item xs={12} sx={CitationRootSx.gridItem}>
          <Box sx={{ minHeight: getWindowInnerHight() }}>
            <Box sx={CitationRootSx.breadcrumbsMainBox}>
              <Box sx={CitationRootSx.breadcrumbsContainer}>
                <CitationBreadcrumbs />
              </Box>

              <Box sx={CitationRootSx.userTutorialBox}>
                {CTAButton}
                <Box
                  component="img"
                  onClick={() => {
                    openNewTab(
                      keywordOverviewTexts?.keywordResearchTutorialsLink
                    );
                  }}
                  src={
                    process.env.PUBLIC_URL +
                    `/static/images/icons/keyword/book-open-02.svg`
                  }
                  alt="icon"
                  sx={CitationRootSx.userTutorialIcon}
                />
                <Typography
                  onClick={() => {
                    openNewTab(GMBTutorialLinks?.localSeoTutorial);
                  }}
                  sx={CitationRootSx.userTutorial}
                >
                  {localSeoTexts?.citationBuilderTutorial}
                </Typography>
              </Box>
            </Box>
            {/* Main Content */}
            <Outlet />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export const CitationRoot = ({ JWTData }: { JWTData: any }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state: citationState } = useCitation();

  const { isEnterprisePlan, isAgency, isLoadingUserDetails } =
    useUserTypeAndPlan();

  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );

  const stripeLoader: any = useSelector(
    (state: RootState) => state.siteAudit.stripeLoader
  );

  const campaignListLength = citationState?.campaignList?.length ?? 0;

  const CTAButton = useMemo(() => {
    if (pathname === '/local-seo/citations/list') {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            navigate('/local-seo/citations');
          }}
          sx={{
            padding: '10px 16px',
            lineHeight: 'var(--line-height-20)',
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: 'var(--font-size-14)',
          }}
        >
          Start New Campaign
        </Button>
      );
    } else if (pathname === '/local-seo/citations') {
      if (campaignListLength === 0) {
        return null;
      }
      return (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            navigate('/local-seo/citations/list');
          }}
          sx={{
            padding: '10px 16px',
            lineHeight: 'var(--line-height-20)',
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: 'var(--font-size-14)',
          }}
        >
          All Campaigns
        </Button>
      );
    }
    return null;
  }, [pathname, campaignListLength, navigate]);

  const allowedUIElement = useMemo(
    () => <AllowedUI CTAButton={CTAButton} />,
    [CTAButton]
  );

  if (JWTData?.adminId === '641cba99df6bcb293db9306c') {
    return allowedUIElement;
  }

  if (!isEnterprisePlan && !stripeLoader && !isLoadingUserDetails) {
    return <Navigate to="/local-seo/citation-waiting-list" />;
  }

  return allowedUIElement;
};
